<script lang="ts" setup>
import { defineEmits, ref } from 'vue'
import VueTurnstile from 'vue-turnstile'
import { useQuasar } from 'quasar'
import { useTakedownNoticeStore } from '../../stores/takedown-notice-store'
import { isRequired, hasMaxLength } from '../../utils/validation'
import * as limits from '../../utils/limits'

const turnstileSiteId = import.meta.env.VITE_TURNSTILE_SITE_KEY

const emit = defineEmits(['save', 'reset'])
const $q = useQuasar()
const takedownNoticeStore = useTakedownNoticeStore()
const turnstileToken = ref<string>('')
const agreesWithPrivacyPolicy = ref(false)
const confirmsTruthfulSubmission = ref(false)

async function onSubmit() {
  if (!agreesWithPrivacyPolicy.value) {
    $q.notify({
      color: 'red-5',
      textColor: 'white',
      icon: 'mdi-alert-box',
      message: 'You must accept the privacy policy.'
    })
    return
  }

  if (!confirmsTruthfulSubmission.value) {
    $q.notify({
      color: 'red-5',
      textColor: 'white',
      icon: 'mdi-alert-box',
      message: 'You must complete the submission declaration.'
    })
    return
  }

  if (!turnstileToken.value) {
    $q.notify({
      color: 'red-5',
      textColor: 'white',
      icon: 'mdi-alert-box',
      message: 'Unable to verify your web browser'
    })
    return
  }

  await takedownNoticeStore.createTakedownNotice(turnstileToken.value!)
  emit('save')
}
</script>

<template>
  <q-form @submit="onSubmit" @reset="emit('reset')" class="q-gutter-md">
    <!-- Requestor Details -->
    <div class="text-subtitle-1">Details of individual submitting request.</div>
    <q-input
      v-model="takedownNoticeStore.takedownNoticeForm.requestorFullName"
      label="Requestor Full Name *"
      lazy-rules
      :rules="[isRequired(), hasMaxLength(limits.fullName)]"
      outlined
      dense
    />
    <q-input
      v-model="takedownNoticeStore.takedownNoticeForm.requestorOrganisationName"
      label="Requestor Organisation Name *"
      lazy-rules
      :rules="[isRequired(), hasMaxLength(limits.organisationName)]"
      outlined
      dense
    />
    <q-input
      v-model="takedownNoticeStore.takedownNoticeForm.requestorTitleOrPosition"
      label="Requestor Title or Position *"
      lazy-rules
      :rules="[isRequired(), hasMaxLength(limits.titleOrPosition)]"
      outlined
      dense
    />
    <q-input
      v-model="takedownNoticeStore.takedownNoticeForm.requestorMailingAddress"
      label="Requestor Mailing Address *"
      lazy-rules
      :rules="[isRequired(), hasMaxLength(limits.address)]"
      outlined
      dense
    />
    <q-input
      v-model="takedownNoticeStore.takedownNoticeForm.requestorPhoneNumber"
      label="Requestor Phone Number *"
      lazy-rules
      :rules="[isRequired(), hasMaxLength(limits.phoneNumber)]"
      outlined
      dense
    />
    <q-input
      v-model="takedownNoticeStore.takedownNoticeForm.requestorEmailAddress"
      label="Requestor Email Address *"
      lazy-rules
      :rules="[isRequired(), hasMaxLength(limits.emailAddress)]"
      outlined
      dense
    />

    <!-- Content -->
    <div class="text-subtitle-1">Details of offending content.</div>
    <q-input
      v-model="takedownNoticeStore.takedownNoticeForm.infringingContentUrl"
      label="Infringing Content URL *"
      lazy-rules
      :rules="[isRequired(), hasMaxLength(limits.url)]"
      outlined
      dense
    />
    <q-input
      v-model="takedownNoticeStore.takedownNoticeForm.originalContentUrl"
      label="Original Content URL *"
      lazy-rules
      :rules="[isRequired(), hasMaxLength(limits.url)]"
      outlined
      dense
    />

    <!-- Copyright Owner -->
    <div class="text-subtitle-1">Details of copyright owner.</div>
    <q-input
      v-model="takedownNoticeStore.takedownNoticeForm.copyrightOwnerFullName"
      label="Copyright Owner Full Name *"
      lazy-rules
      :rules="[isRequired(), hasMaxLength(limits.fullName)]"
      outlined
      dense
    />
    <q-input
      v-model="takedownNoticeStore.takedownNoticeForm.copyrightOwnerPhoneNumber"
      label="Copyright Owner Phone Number *"
      lazy-rules
      :rules="[isRequired(), hasMaxLength(limits.phoneNumber)]"
      outlined
      dense
    />
    <q-input
      v-model="takedownNoticeStore.takedownNoticeForm.copyrightOwnerEmailAddress"
      label="Copyright Owner Email Address *"
      lazy-rules
      :rules="[isRequired(), hasMaxLength(limits.emailAddress)]"
      outlined
      dense
    />

    <!-- Declaration -->
    <div class="q-gutter-sm">
      <q-checkbox
        v-model="agreesWithPrivacyPolicy"
        label="I have read and accept the Road Footage privacy policy."
      />
    </div>
    <div class="q-gutter-sm">
      <q-checkbox
        v-model="confirmsTruthfulSubmission"
        label="I declare (or certify, verify, or state) under penalty of perjury that the foregoing is true and correct."
      />
    </div>

    <vue-turnstile :site-key="turnstileSiteId" v-model="turnstileToken" theme="light" />
    <div>
      <q-btn
        label="Submit"
        type="submit"
        color="primary"
        :disable="takedownNoticeStore.isSaving"
        :loading="takedownNoticeStore.isSaving"
      />
      <q-btn
        label="Cancel"
        type="reset"
        flat
        class="q-ml-sm"
        :disable="takedownNoticeStore.isSaving"
      />
    </div>
  </q-form>
</template>
